/**
 * Live Notifications Component
 * Display real-time notifications for birdies, eagles, hole-in-ones.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, Trophy, Star } from 'lucide-react';
import { getNotificationStyle, getRandomPencilWhipMessage } from '../utils/outingHelpers';

export default function LiveNotifications({ outingId, onDismiss }) {
  const [activeNotification, setActiveNotification] = useState(null);
  const [queue, setQueue] = useState([]);

  // Show next notification from queue
  useEffect(() => {
    if (!activeNotification && queue.length > 0) {
      const next = queue[0];
      setActiveNotification(next);
      setQueue(prev => prev.slice(1));
      
      // Auto dismiss after delay
      const timeout = next.type === 'hole_in_one' ? 8000 : 5000;
      setTimeout(() => {
        setActiveNotification(null);
      }, timeout);
    }
  }, [activeNotification, queue]);

  // External method to add notifications
  const addNotification = useCallback((notification) => {
    setQueue(prev => [...prev, notification]);
  }, []);

  // Expose addNotification via window for external calls
  useEffect(() => {
    window.addOutingNotification = addNotification;
    return () => {
      delete window.addOutingNotification;
    };
  }, [addNotification]);

  if (!activeNotification) return null;

  const style = getNotificationStyle(activeNotification.type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
      <div 
        className={`
          ${style.bgClass} ${style.borderClass}
          border-2 rounded-2xl p-6 max-w-md w-full
          pointer-events-auto
          animate-in zoom-in-95 fade-in duration-300
          ${style.intensity === 'legendary' ? 'animate-pulse' : ''}
        `}
        onClick={() => setActiveNotification(null)}
      >
        {/* Close Button */}
        <button 
          className="absolute top-3 right-3 text-white/50 hover:text-white"
          onClick={() => setActiveNotification(null)}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="text-center">
          {/* Icon */}
          <div className="text-6xl mb-4 animate-bounce">
            {activeNotification.type === 'hole_in_one' && '🏆'}
            {activeNotification.type === 'eagle' && '🦅'}
            {activeNotification.type === 'birdie' && '🐦'}
            {activeNotification.type === 'ctp_submission' && '🎯'}
          </div>

          {/* Title */}
          <h2 className={`text-2xl font-black uppercase tracking-tight mb-2 ${
            activeNotification.type === 'hole_in_one' ? 'text-yellow-400' :
            activeNotification.type === 'eagle' ? 'text-purple-400' :
            activeNotification.type === 'birdie' ? 'text-green-400' : 'text-blue-400'
          }`}>
            {activeNotification.type === 'hole_in_one' && 'HOLE IN ONE!!!'}
            {activeNotification.type === 'eagle' && 'EAGLE ALERT!'}
            {activeNotification.type === 'birdie' && 'BIRDIE!'}
            {activeNotification.type === 'ctp_submission' && 'CTP SUBMISSION'}
          </h2>

          {/* Message */}
          <p className="text-white text-lg font-semibold">
            {activeNotification.message}
          </p>

          {/* Team & Hole */}
          <div className="mt-4 flex items-center justify-center gap-4 text-zinc-300">
            <span className="flex items-center gap-1">
              <Star className="w-4 h-4" />
              {activeNotification.team_name}
            </span>
            <span>Hole {activeNotification.hole}</span>
          </div>
        </div>

        {/* Legendary Effect for Hole-in-One */}
        {activeNotification.type === 'hole_in_one' && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-ping"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${1 + Math.random()}s`
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Pencil Whipping Popup Component
export function PencilWhipPopup({ show, onConfirm, onRecount }) {
  const [message] = useState(getRandomPencilWhipMessage);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[#1A1A1A] border border-white/20 rounded-2xl p-6 max-w-sm w-full animate-in zoom-in-95">
        <div className="text-center">
          <div className="text-5xl mb-4">🕵️</div>
          <h3 className="text-xl font-bold text-white mb-2">
            {message.text}
          </h3>
          <p className="text-zinc-400 text-sm mb-6">
            {message.subtext}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onRecount}
              className="flex-1 py-2 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Let me recount
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-2 px-4 bg-[#007AFF] hover:bg-[#38BDF8] text-white rounded-lg font-semibold transition-colors"
            >
              I&apos;m sure!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
